import Badge from "@/assets/svg/vvv/badge.svg";
import Quote from "@/assets/svg/vvv/quote.svg";
import GradientBg from "@/assets/svg/vvv/testimonials-bg.svg";
import { animate, motion, useMotionValue } from "framer-motion";
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import VVVPrimaryButton from "../design-system/vvv/Button";
import { Separator } from "../ui/separator";

export default function VVVTestimonials() {
  const testimonies = [
    {
      thumbnail:
        "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/vvv/adley.svg",
      message: "Additional exposure is always a win.",
      name: "ADLEY",
      followers: "12M",
    },
    {
      thumbnail:
        "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/vvv/francisco.svg",
      message: "[...] this has changed my life. I can now pay my bills.",
      name: "FRANCISCO",
      followers: "5M",
    },
    {
      thumbnail:
        "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/vvv/foxadm.svg",
      message: "I’ve significantly increased my YouTube income.",
      name: "foxADM",
      followers: "120K",
    },
    {
      thumbnail:
        "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/vvv/galaxity.svg",
      message:
        "I highly recommend working with them, better than other UGC companies.",
      name: "Galaxity",
      followers: "65K",
    },
    {
      thumbnail:
        "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/vvv/focus.svg",
      message: "Amazing earning opportunity for regular creators.",
      name: "Focus TikTok",
      followers: "70K",
    },
    {
      thumbnail:
        "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/vvv/bralcon.svg",
      message: "Excited to inspire others to join this fantastic program.",
      name: "Bralcon",
      followers: "70K",
    },
    {
      thumbnail:
        "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/vvv/elgatomemes.svg",
      message: "I've been working with them for over a year now—0 complains.",
      name: "EIGatoMemes",
      followers: "240K",
    },
    {
      thumbnail:
        "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/vvv/little-pets-media.svg",
      message: "My life has flourished significantly thanks to them.",
      name: "Little Pets Media",
      followers: "60K",
    },
    {
      thumbnail:
        "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/vvv/averstrike.svg",
      message: "Generated over 100k USD. Highly recommended.",
      name: "Averstrike",
      followers: "500K",
    },
  ];

  const x = useMotionValue(0);
  const containerRef = useRef(null);

  const CARD_WIDTH = 360;
  const TOTAL_WIDTH = testimonies.length * CARD_WIDTH;
  const SCROLL_SPEED = 40;
  const DRAG_SENSITIVITY = 0.6;

  useEffect(() => {
    let offset = 0;
    let isDragging = false;
    let dragStartX = 0;
    let controls;

    const container = containerRef.current;

    const startAutoScroll = () => {
      controls = animate(offset, -TOTAL_WIDTH, {
        duration: (TOTAL_WIDTH - Math.abs(offset)) / SCROLL_SPEED,
        ease: "linear",
        onUpdate: (latest) => {
          offset = latest;
          x.set(offset);

          if (offset <= -TOTAL_WIDTH) {
            offset = 0;
            x.set(0);
          }
        },
      });
    };

    startAutoScroll();

    const handleMouseDown = (e) => {
      isDragging = true;
      dragStartX = e.clientX;
      controls?.stop();
    };

    const handleMouseUp = () => {
      if (!isDragging) return;
      isDragging = false;
      startAutoScroll();
    };

    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const dragDistance = e.clientX - dragStartX;

      offset += dragDistance * DRAG_SENSITIVITY;

      if (offset > 0) offset = 0;
      if (offset < -TOTAL_WIDTH) offset = -TOTAL_WIDTH;

      x.set(offset);
      dragStartX = e.clientX;
    };

    container.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      controls?.stop();
      container.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [x, TOTAL_WIDTH]);

  return (
    <div id="testimonials" className="relative overflow-hidden">
      <img
        src={GradientBg}
        className="absolute -top-52 left-0"
        alt="Gradient Background"
      />

      <div className="py-6 lg:py-12 space-y-20 mx-auto 4xl:max-w-[100rem] relative z-[2]">
        <div className="space-y-2 text-center lg:text-left px-6 lg:px-12">
          <p className="text-white/50">Our Partners</p>
          <h1 className="text-4xl lg:text-5xl vvv-font" style={{ lineHeight: 1.4 }}>
            Trusted by Creators and <br /> Businesses Worldwide
          </h1>
        </div>

        <div
          ref={containerRef}
          className="relative overflow-hidden cursor-grab active:cursor-grabbing select-none"
        >
          <motion.div style={{ x }} className="flex">
            {[...testimonies, ...testimonies].map((item, index) => (
              <div
                key={index}
                className="p-8 relative bg-gradient-to-l from-zinc-800/0 to-neutral-800 rounded-[20px] shadow-[0px_24px_56px_0px_rgba(0,0,0,0.16)] outline outline-1 outline-offset-[-1px] outline-neutral-700 backdrop-blur-xl overflow-hidden flex flex-col gap-6 mx-2 shrink-0"
                style={{
                  width: "340px",
                  height: "380px",
                }}
              >
                <div className="flex items-center gap-5">
                  <img
                    src={item.thumbnail}
                    alt={item.name}
                    className="shadow-lg w-14 h-14 rounded-full object-fill"
                  />
                  <div className="space-y-0">
                    <div className="flex items-center gap-2">
                      <h2 className="font-semibold text-lg">{item.name}</h2>
                      <img src={Badge} alt="Badge Icon" />
                    </div>
                    <p className="text-sm text-white/50">
                      {item.followers}+ followers
                    </p>
                  </div>
                </div>

                <Separator className="bg-[#434242]" />

                <img src={Quote} className="max-w-10" alt="Quote Icon" />

                <h1 className="line-clamp-5 leading-relaxed">{item.message}</h1>
              </div>
            ))}
          </motion.div>
        </div>

        <Link to="/esign" className="w-full flex justify-center">
          <VVVPrimaryButton name="Be the next success story" />
        </Link>
      </div>
    </div>
  );
}
